import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronDown, ChevronRight, Pencil, RotateCcw, Sparkles, Check, X as XIcon, Loader2 } from 'lucide-react';

import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from '../components/ui/sonner';
import { COUNTRIES } from '../data/countries';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Friendly labels for category keys returned by /quiz/questions
const SECTION_LABELS = {
  general: 'About you',
  lifestyle: 'Lifestyle',
  health_conditions: 'Health conditions',
  prostate: 'Prostate health',
  sleep: 'Sleep',
  energy: 'Energy',
  stress: 'Stress',
  digestion: 'Digestion',
  immunity: 'Immunity',
  joints: 'Joints',
  hair_skin: 'Hair & skin',
  brain_focus: 'Brain & focus',
  mood: 'Mood',
  heart: 'Heart',
  weight: 'Weight',
  vision: 'Vision',
  menopause: 'Menopause',
  fertility: 'Fertility',
};

const SECTION_ORDER = [
  'general', 'lifestyle', 'health_conditions', 'prostate', 'sleep', 'energy',
  'stress', 'digestion', 'immunity', 'joints', 'hair_skin', 'brain_focus',
  'mood', 'heart', 'weight', 'vision', 'menopause', 'fertility',
];

// ─── Prefill normalization (mirrors QuizPage.js so behaviour stays identical) ─
function normalizePrefill(rawAnswers, questions) {
  const multiSelectIds = new Set(questions.filter(q => q.type === 'multi_select').map(q => q.id));
  const heightIds = new Set(questions.filter(q => q.type === 'height_input').map(q => q.id));
  const weightIds = new Set(questions.filter(q => q.type === 'weight_input').map(q => q.id));
  const optionsByQuestion = {};
  questions.filter(q => q.type === 'multi_select').forEach(q => {
    optionsByQuestion[q.id] = q.options || [];
  });

  const normalized = { ...rawAnswers };
  for (const qid of Object.keys(normalized)) {
    const v = normalized[qid];

    if (heightIds.has(qid) && typeof v === 'string' && v.length > 0) {
      const cmMatch = v.match(/^\s*(\d+(?:\.\d+)?)\s*cm\s*$/i);
      const ftInMatch = v.match(/^\s*(\d+)\s*'\s*(\d+(?:\.\d+)?)?\s*"?\s*$/);
      if (cmMatch) normalized[qid] = { cm: cmMatch[1], unit: 'cm' };
      else if (ftInMatch) normalized[qid] = { feet: ftInMatch[1], inches: ftInMatch[2] || '0', unit: 'ft-in' };
      else normalized[qid] = {};
      continue;
    }
    if (weightIds.has(qid) && typeof v === 'string' && v.length > 0) {
      const m = v.match(/^\s*(\d+(?:\.\d+)?)\s*(lbs?|kg|kilograms?|pounds?)?\s*$/i);
      if (m) {
        const rawUnit = (m[2] || 'lbs').toLowerCase();
        const unit = rawUnit.startsWith('k') ? 'kg' : 'lbs';
        normalized[qid] = { value: m[1], unit };
      } else normalized[qid] = {};
      continue;
    }
    if (!multiSelectIds.has(qid)) continue;

    let arr;
    if (Array.isArray(v)) arr = v;
    else if (typeof v === 'string' && v.length > 0) arr = v.split(',').map(s => s.trim()).filter(Boolean);
    else arr = [];
    const validSet = new Set(optionsByQuestion[qid] || []);
    const validLower = new Map((optionsByQuestion[qid] || []).map(o => [String(o).toLowerCase(), o]));
    const filtered = [];
    const seen = new Set();
    arr.forEach(item => {
      const s = String(item).trim();
      if (!s) return;
      const canonical = validSet.has(s) ? s : validLower.get(s.toLowerCase());
      if (!canonical) return;
      if (seen.has(canonical)) return;
      seen.add(canonical);
      filtered.push(canonical);
    });
    normalized[qid] = filtered;
  }
  return normalized;
}

function formatAnswersForSubmit(answers) {
  return Object.entries(answers).map(([question_id, answer]) => {
    if (answer && typeof answer === 'object' && answer.value !== undefined) {
      return { question_id, answer: `${answer.value} ${answer.unit}` };
    }
    if (answer && typeof answer === 'object' && (answer.feet !== undefined || answer.cm !== undefined)) {
      if (answer.unit === 'cm') return { question_id, answer: `${answer.cm} cm` };
      return { question_id, answer: `${answer.feet || 0}'${answer.inches || 0}"` };
    }
    if (Array.isArray(answer)) return { question_id, answer: answer.join(', ') };
    return { question_id, answer };
  });
}

function answerDisplay(answer, question) {
  if (answer === undefined || answer === null || answer === '') return <span className="italic text-muted-foreground">No answer</span>;
  if (Array.isArray(answer)) return answer.length === 0
    ? <span className="italic text-muted-foreground">No answer</span>
    : answer.join(', ');
  if (typeof answer === 'object') {
    if (answer.value !== undefined) return `${answer.value} ${answer.unit}`;
    if (answer.unit === 'cm' && answer.cm) return `${answer.cm} cm`;
    if (answer.feet !== undefined || answer.inches !== undefined) return `${answer.feet || 0}'${answer.inches || 0}"`;
    return JSON.stringify(answer);
  }
  return String(answer);
}

// ─── Country picker (used inside the edit modal) ────────────────────────────
function CountryEditor({ value, onChange }) {
  const [search, setSearch] = useState('');
  const list = search
    ? COUNTRIES.filter(c => c.toLowerCase().includes(search.toLowerCase()))
    : COUNTRIES;
  return (
    <div className="space-y-3">
      <Input
        autoFocus
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Type to search countries…"
        data-testid="edit-country-search"
      />
      <div className="max-h-72 overflow-y-auto rounded-lg border border-border bg-white divide-y divide-border">
        {list.length === 0 && (
          <p className="p-3 text-sm text-muted-foreground">No matches</p>
        )}
        {list.map(c => {
          const selected = value === c;
          return (
            <button
              key={c}
              type="button"
              onClick={() => onChange(c)}
              className={`w-full text-left px-3 py-2.5 text-sm transition ${selected ? 'bg-emerald-50 text-emerald-700 font-semibold' : 'hover:bg-muted/50'}`}
              data-testid={`edit-country-option-${c}`}
            >
              {c}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Inline editor for a single question (used inside the modal) ─────────────
function QuestionEditor({ question, value, onChange }) {
  if (!question) return null;
  // Backend only sets `type` for special inputs (multi_select, height_input,
  // weight_input, country_select, slider). Everything else with an `options`
  // array is an implicit single-select radio.
  let type = question.type;
  if (!type) {
    if (Array.isArray(question.options) && question.options.length > 0) type = 'radio';
    else type = 'text';
  }

  if (type === 'country_select') {
    return <CountryEditor value={value} onChange={onChange} />;
  }

  if (type === 'radio') {
    return (
      <div className="space-y-2">
        {(question.options || []).map(opt => (
          <label key={opt} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${value === opt ? 'border-emerald-500 bg-emerald-50' : 'border-border'}`} data-testid={`edit-radio-${opt}`}>
            <input type="radio" name={question.id} value={opt} checked={value === opt} onChange={() => onChange(opt)} />
            <span className="text-sm">{opt}</span>
          </label>
        ))}
      </div>
    );
  }

  if (type === 'multi_select') {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (opt) => {
      let next;
      if (opt === 'None' || opt === 'Not applicable') {
        next = arr.includes(opt) ? [] : [opt];
      } else {
        next = arr.includes(opt)
          ? arr.filter(x => x !== opt)
          : [...arr.filter(x => x !== 'None' && x !== 'Not applicable'), opt];
      }
      onChange(next);
    };
    return (
      <div className="space-y-2">
        {(question.options || []).map(opt => {
          const checked = arr.includes(opt);
          return (
            <label key={opt} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${checked ? 'border-emerald-500 bg-emerald-50' : 'border-border'}`} data-testid={`edit-cb-${opt}`}>
              <input type="checkbox" checked={checked} onChange={() => toggle(opt)} />
              <span className="text-sm">{opt}</span>
            </label>
          );
        })}
      </div>
    );
  }

  if (type === 'slider') {
    const num = typeof value === 'number' ? value : (parseInt(value) || question.min || 1);
    return (
      <div className="space-y-3">
        <div className="text-center text-3xl font-bold text-emerald-600" data-testid="edit-slider-value">{num}</div>
        <input
          type="range"
          min={question.min ?? 1}
          max={question.max ?? 10}
          step={1}
          value={num}
          onChange={(e) => onChange(parseInt(e.target.value))}
          className="w-full"
        />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{question.min_label || question.min || 1}</span>
          <span>{question.max_label || question.max || 10}</span>
        </div>
      </div>
    );
  }

  if (type === 'weight_input') {
    const cur = value || {};
    return (
      <div className="flex gap-2 items-end">
        <div className="flex-1">
          <label className="text-xs text-muted-foreground">Weight</label>
          <Input type="number" value={cur.value || ''} onChange={(e) => onChange({ value: e.target.value, unit: cur.unit || 'lbs' })} />
        </div>
        <select
          value={cur.unit || 'lbs'}
          onChange={(e) => onChange({ value: cur.value || '', unit: e.target.value })}
          className="border rounded-md px-3 py-2 text-sm"
        >
          <option value="lbs">lbs</option>
          <option value="kg">kg</option>
        </select>
      </div>
    );
  }

  if (type === 'height_input') {
    const cur = value || {};
    const isCm = cur.unit === 'cm';
    return (
      <div className="space-y-3">
        <select
          value={cur.unit || 'ft-in'}
          onChange={(e) => onChange({ ...cur, unit: e.target.value })}
          className="border rounded-md px-3 py-2 text-sm"
        >
          <option value="ft-in">ft / in</option>
          <option value="cm">cm</option>
        </select>
        {isCm ? (
          <Input type="number" placeholder="cm" value={cur.cm || ''} onChange={(e) => onChange({ ...cur, cm: e.target.value, unit: 'cm' })} />
        ) : (
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-xs text-muted-foreground">Feet</label>
              <Input type="number" value={cur.feet || ''} onChange={(e) => onChange({ ...cur, feet: e.target.value, unit: 'ft-in' })} />
            </div>
            <div className="flex-1">
              <label className="text-xs text-muted-foreground">Inches</label>
              <Input type="number" value={cur.inches || ''} onChange={(e) => onChange({ ...cur, inches: e.target.value, unit: 'ft-in' })} />
            </div>
          </div>
        )}
      </div>
    );
  }

  // Fallback: free-text
  return <Input value={value || ''} onChange={(e) => onChange(e.target.value)} />;
}

// ─── Draft persistence (localStorage) ────────────────────────────────────────
// Persists in-progress edits so a user who accidentally closes the PWA mid-edit
// (memory eviction, killed background, app reload) can resume their work.
// Drafts are scoped to the user AND tied to a fingerprint of their baseline
// (originalAnswers) — if their backend assessment changed since the draft was
// saved, we treat the draft as stale and ignore it.
const DRAFT_STORAGE_PREFIX = 'vitala:edit-answers-draft:';
const DRAFT_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

function fingerprintAnswers(obj) {
  // Stable JSON: sort keys + recursively normalize arrays (multi-selects) so
  // baseline comparison is order-independent.
  const sortedKeys = Object.keys(obj || {}).sort();
  const norm = {};
  for (const k of sortedKeys) {
    const v = obj[k];
    if (Array.isArray(v)) {
      norm[k] = [...v].sort();
    } else if (v && typeof v === 'object') {
      norm[k] = JSON.parse(JSON.stringify(v));
    } else {
      norm[k] = v;
    }
  }
  // FNV-1a-ish hash, sufficient for change detection
  const s = JSON.stringify(norm);
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return String(h);
}

function readDraft(userId) {
  try {
    const raw = localStorage.getItem(DRAFT_STORAGE_PREFIX + userId);
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (!d || !d.savedAt) return null;
    if (Date.now() - new Date(d.savedAt).getTime() > DRAFT_MAX_AGE_MS) return null;
    return d;
  } catch { return null; }
}

function writeDraft(userId, payload) {
  try {
    localStorage.setItem(DRAFT_STORAGE_PREFIX + userId, JSON.stringify(payload));
  } catch { /* quota or private mode — ignore */ }
}

function clearDraft(userId) {
  try {
    localStorage.removeItem(DRAFT_STORAGE_PREFIX + userId);
  } catch { /* ignore */ }
}

export default function EditAnswersPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading, getAuthHeader } = useAuth();

  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [originalAnswers, setOriginalAnswers] = useState({});
  const [medications, setMedications] = useState([]);
  const [currentSupplements, setCurrentSupplements] = useState([]);
  const [openSections, setOpenSections] = useState({}); // {category: bool}
  const [editingQid, setEditingQid] = useState(null);
  const [draftValue, setDraftValue] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Resume-draft offer: { savedAt, draftAnswers } — populated if a non-stale
  // draft is found in localStorage on mount.
  const [pendingDraft, setPendingDraft] = useState(null);
  // Once we hydrate the baseline we can compute the baseline fingerprint and
  // start auto-saving drafts.
  const [baselineFp, setBaselineFp] = useState(null);

  useEffect(() => {
    if (authLoading) return; // wait for hydration
    if (!user) {
      navigate('/auth');
      return;
    }
    (async () => {
      try {
        const [qRes, aRes] = await Promise.all([
          axios.get(`${API}/quiz/questions`),
          axios.get(`${API}/assessment/latest-answers`, { headers: getAuthHeader() })
        ]);
        const qs = qRes.data.questions || [];
        setQuestions(qs);
        if (aRes.data.has_previous) {
          const norm = normalizePrefill(aRes.data.answers || {}, qs);
          const fp = fingerprintAnswers(norm);
          setOriginalAnswers(JSON.parse(JSON.stringify(norm)));
          setBaselineFp(fp);
          setMedications(aRes.data.medications || []);
          setCurrentSupplements(aRes.data.current_supplements || []);
          // Look for a saved draft. Only offer to resume if the baseline still
          // matches (otherwise the draft was made against different prior data).
          const draft = readDraft(user.id);
          if (draft && draft.baselineFp === fp && draft.answers) {
            setAnswers(JSON.parse(JSON.stringify(norm))); // start with baseline
            setPendingDraft({ savedAt: draft.savedAt, draftAnswers: draft.answers });
          } else {
            if (draft && draft.baselineFp !== fp) clearDraft(user.id); // stale
            setAnswers(norm);
          }
        } else {
          toast.error('No previous assessment found — take the full quiz first.');
          navigate('/quiz');
          return;
        }
      } catch (e) {
        console.error(e);
        toast.error('Failed to load your answers');
      } finally {
        setLoading(false);
      }
    })();
  }, [user, authLoading]);

  // Auto-save the current edit set as a draft so the user can resume after
  // accidentally closing the PWA. Skips while loading, when no baseline is
  // ready yet, or while a resume offer is still on screen (no edits yet).
  useEffect(() => {
    if (loading || !user || !baselineFp || pendingDraft) return;
    // Only persist if there's at least one edit; otherwise clear any stale draft.
    const currentFp = fingerprintAnswers(answers);
    if (currentFp === baselineFp) {
      clearDraft(user.id);
      return;
    }
    const t = setTimeout(() => {
      writeDraft(user.id, {
        baselineFp,
        answers,
        savedAt: new Date().toISOString(),
      });
    }, 600);
    return () => clearTimeout(t);
  }, [answers, baselineFp, loading, user, pendingDraft]);

  // Filter questions by gender + conditional rules (mirrors QuizPage logic)
  const filteredQuestions = useMemo(() => {
    if (!questions.length) return [];
    const genderAnswer = answers['gender'];
    return questions.filter(q => {
      if (genderAnswer === 'Male' && (q.category === 'menopause' || q.category === 'fertility')) return false;
      if (q.condition) {
        const ua = answers[q.condition.question_id];
        if (!ua) return false;
        if (q.condition.contains_any) {
          const vals = q.condition.contains_any;
          if (Array.isArray(ua)) return ua.some(a => vals.some(v => a.includes(v)));
          return vals.some(v => ua.includes(v));
        }
        if (q.condition.contains) {
          if (Array.isArray(ua)) return ua.some(a => a.includes(q.condition.contains));
          return ua.includes(q.condition.contains);
        }
        if (q.condition.equals) return ua === q.condition.equals;
      }
      return true;
    });
  }, [questions, answers]);

  // Group by category, preserving original order from filteredQuestions
  const grouped = useMemo(() => {
    const map = {};
    for (const q of filteredQuestions) {
      const cat = q.category || 'other';
      (map[cat] = map[cat] || []).push(q);
    }
    const ordered = SECTION_ORDER.filter(s => map[s]);
    const leftover = Object.keys(map).filter(c => !ordered.includes(c));
    return [...ordered, ...leftover].map(cat => ({ category: cat, questions: map[cat] }));
  }, [filteredQuestions]);

  const isEdited = (qid) => JSON.stringify(originalAnswers[qid]) !== JSON.stringify(answers[qid]);
  const editedCount = useMemo(() => {
    return Object.keys(answers).filter(qid => isEdited(qid)).length;
  }, [answers, originalAnswers]);

  const openEditor = (qid) => {
    setEditingQid(qid);
    const cur = answers[qid];
    // Deep-clone so cancel restores cleanly
    setDraftValue(cur && typeof cur === 'object' ? JSON.parse(JSON.stringify(cur)) : cur);
  };

  const cancelEdit = () => {
    setEditingQid(null);
    setDraftValue(null);
  };

  const saveEdit = () => {
    if (!editingQid) return;
    setAnswers(prev => ({ ...prev, [editingQid]: draftValue }));
    setEditingQid(null);
    setDraftValue(null);
  };

  const revertSingle = (qid) => {
    setAnswers(prev => ({ ...prev, [qid]: originalAnswers[qid] }));
  };

  const discardAllEdits = () => {
    if (editedCount === 0) return;
    if (!window.confirm(`Discard all ${editedCount} edit${editedCount !== 1 ? 's' : ''}?`)) return;
    setAnswers(JSON.parse(JSON.stringify(originalAnswers)));
    if (user) clearDraft(user.id);
    toast.success('All edits discarded');
  };

  const resumeDraft = () => {
    if (!pendingDraft) return;
    setAnswers(JSON.parse(JSON.stringify(pendingDraft.draftAnswers)));
    setPendingDraft(null);
    toast.success('Resumed your in-progress edits');
  };

  const dismissDraft = () => {
    if (user) clearDraft(user.id);
    setPendingDraft(null);
  };

  const submitRecompute = async () => {
    if (editedCount === 0) {
      toast.info('No edits to save');
      return;
    }
    setSubmitting(true);
    try {
      const formatted = formatAnswersForSubmit(answers);
      const res = await axios.post(`${API}/assessment`, {
        answers: formatted,
        medications,
        current_supplements: currentSupplements,
      }, { headers: getAuthHeader() });
      const id = res.data?.id || res.data?.assessment_id;
      if (user) clearDraft(user.id);
      toast.success('Recomputing your personalized recommendations…');
      navigate(id ? `/results/${id}?changed=1` : '/dashboard');
    } catch (e) {
      console.error(e);
      toast.error('Failed to recompute — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
      </div>
    );
  }

  const editingQuestion = editingQid ? questions.find(q => q.id === editingQid) : null;

  return (
    <div className="min-h-screen bg-background pb-48 md:pb-32" data-testid="edit-answers-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg hover:bg-muted"
            data-testid="edit-answers-back-btn"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-[#1A1A1A]">Edit your answers</h1>
            <p className="text-xs text-muted-foreground">
              Tap any question to update it. We&apos;ll recompute your plan once you&apos;re done.
            </p>
          </div>
        </div>

        {/* Resume-draft banner — shown if a recent in-progress edit set was found */}
        {pendingDraft && (() => {
          // Quick diff: how many answers changed in the saved draft vs baseline.
          const draftEdits = Object.keys(pendingDraft.draftAnswers || {}).filter(
            qid => JSON.stringify(originalAnswers[qid]) !== JSON.stringify(pendingDraft.draftAnswers[qid])
          ).length;
          const ago = (() => {
            const ms = Date.now() - new Date(pendingDraft.savedAt).getTime();
            const mins = Math.floor(ms / 60000);
            if (mins < 1) return 'just now';
            if (mins < 60) return `${mins} min ago`;
            const hrs = Math.floor(mins / 60);
            if (hrs < 24) return `${hrs}h ago`;
            const days = Math.floor(hrs / 24);
            return `${days}d ago`;
          })();
          return (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-3 sm:p-4"
              data-testid="resume-draft-banner"
            >
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Pencil className="w-4 h-4 text-amber-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-amber-900">
                    You have unfinished edits
                  </p>
                  <p className="text-xs text-amber-800 mt-0.5">
                    {draftEdits} answer{draftEdits !== 1 ? 's were' : ' was'} edited but never saved · {ago}
                  </p>
                  <div className="flex gap-2 mt-2.5">
                    <Button
                      size="sm"
                      onClick={resumeDraft}
                      className="bg-amber-600 hover:bg-amber-700 text-white h-8 text-xs"
                      data-testid="resume-draft-btn"
                    >
                      Resume editing
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={dismissDraft}
                      className="text-amber-800 hover:bg-amber-100 h-8 text-xs"
                      data-testid="dismiss-draft-btn"
                    >
                      Discard draft
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })()}

        {/* Sections */}
        <div className="space-y-3">
          {grouped.map(({ category, questions: qs }) => {
            const editedInSection = qs.filter(q => isEdited(q.id)).length;
            const isOpen = openSections[category] !== false;
            return (
              <div key={category} className="bg-white rounded-xl border border-border overflow-hidden" data-testid={`section-${category}`}>
                <button
                  onClick={() => setOpenSections(p => ({ ...p, [category]: !isOpen }))}
                  className="w-full flex items-center justify-between p-3 sm:p-4 text-left hover:bg-muted/30 transition"
                  data-testid={`section-toggle-${category}`}
                >
                  <div className="flex items-center gap-2">
                    {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    <span className="font-medium text-[#1A1A1A]">{SECTION_LABELS[category] || category}</span>
                    <span className="text-xs text-muted-foreground">({qs.length})</span>
                  </div>
                  {editedInSection > 0 && (
                    <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                      {editedInSection} edited
                    </span>
                  )}
                </button>
                {isOpen && (
                  <div className="border-t border-border divide-y divide-border">
                    {qs.map(q => {
                      const edited = isEdited(q.id);
                      return (
                        <div
                          key={q.id}
                          className={`p-3 sm:p-4 flex items-start gap-3 ${edited ? 'bg-amber-50/40' : ''}`}
                          data-testid={`edit-row-${q.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-[#1A1A1A] leading-snug">{q.question}</p>
                            <p className="text-sm text-muted-foreground mt-1 break-words" data-testid={`edit-answer-${q.id}`}>
                              {answerDisplay(answers[q.id], q)}
                            </p>
                            {edited && (
                              <div className="mt-1 flex items-center gap-2">
                                <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                                  <Pencil className="w-3 h-3" /> Edited
                                </span>
                                <button
                                  onClick={() => revertSingle(q.id)}
                                  className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5"
                                  data-testid={`edit-revert-${q.id}`}
                                >
                                  <RotateCcw className="w-3 h-3" /> Revert
                                </button>
                              </div>
                            )}
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditor(q.id)}
                            className="shrink-0 h-8 text-xs"
                            data-testid={`edit-btn-${q.id}`}
                          >
                            <Pencil className="w-3 h-3 mr-1" /> Edit
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Persistent recompute banner — must sit ABOVE the mobile bottom nav (h-16, z-40).
          On md+ the bottom nav is hidden, so we anchor at bottom-0; on mobile we shift
          up by the nav height + safe-area inset so the banner is never hidden. */}
      <motion.div
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="fixed left-0 right-0 z-50 bg-white border-t border-border shadow-[0_-2px_10px_rgba(0,0,0,0.08)] bottom-16 md:bottom-0"
        style={{ marginBottom: 'env(safe-area-inset-bottom, 0px)' }}
        data-testid="recompute-banner"
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-2 sm:gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-[#1A1A1A]">
              {editedCount === 0 ? 'No changes yet' : `${editedCount} answer${editedCount !== 1 ? 's' : ''} edited`}
            </p>
            <p className="text-[11px] text-muted-foreground hidden sm:block">
              Recompute when you&apos;re ready — you can keep editing freely.
            </p>
          </div>
          {editedCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={discardAllEdits}
              disabled={submitting}
              className="text-muted-foreground hover:text-rose-700 hover:bg-rose-50 shrink-0"
              data-testid="discard-edits-btn"
            >
              Discard
            </Button>
          )}
          <Button
            onClick={submitRecompute}
            disabled={submitting || editedCount === 0}
            className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
            data-testid="recompute-btn"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Recomputing…</>
            ) : (
              <><Sparkles className="w-4 h-4 mr-2" /> Recompute plan</>
            )}
          </Button>
        </div>
      </motion.div>

      {/* Edit modal */}
      <Dialog open={!!editingQid} onOpenChange={(open) => { if (!open) cancelEdit(); }}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto" data-testid="edit-modal">
          {editingQuestion && (
            <>
              <DialogHeader>
                <DialogTitle className="text-base font-semibold text-left leading-snug pr-6">
                  {editingQuestion.question}
                </DialogTitle>
                {editingQuestion.helper_text && (
                  <p className="text-xs text-red-600 italic text-left">{editingQuestion.helper_text}</p>
                )}
              </DialogHeader>
              <div className="py-2">
                <QuestionEditor question={editingQuestion} value={draftValue} onChange={setDraftValue} />
              </div>
              <DialogFooter className="flex gap-2 sm:gap-0">
                <Button variant="outline" onClick={cancelEdit} data-testid="edit-cancel-btn">
                  <XIcon className="w-4 h-4 mr-1" /> Cancel
                </Button>
                <Button
                  onClick={saveEdit}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="edit-save-btn"
                >
                  <Check className="w-4 h-4 mr-1" /> Save
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
